import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import IconClose from '../IconClose/IconClose';
import { getHelpContent } from './helpContentRegistry';

import css from './HelpFlyout.module.css';

const KEY_CODE_ESCAPE = 27;
export const HELP_FLYOUT_SCROLL_LOCK_ID = 'HelpFlyout';

const Portal = ({ portalRoot, children }) => {
  const elRef = useRef(document.createElement('div'));

  useEffect(() => {
    const el = elRef.current;
    portalRoot.appendChild(el);
    return () => {
      portalRoot.removeChild(el);
    };
  }, [portalRoot]);

  return ReactDOM.createPortal(children, elRef.current);
};

/**
 * Right-side help flyout. Resolves static content by contentId from the registry.
 * Registry entries may define tabs; visibility is gated per tab by currentUser
 * and the active tab's component is rendered in the body.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {string|null} props.contentId
 * @param {string|null} [props.requestedTabId] - Tab to open initially (falls back to first visible)
 * @param {Function} props.onClose
 * @param {Function} props.onManageDisableScrolling
 * @param {string?} props.className
 */
const HelpFlyout = props => {
  const {
    isOpen,
    contentId,
    requestedTabId = null,
    onClose,
    onManageDisableScrolling,
    className,
  } = props;
  const intl = useIntl();
  const panelRef = useRef(null);
  const [portalRoot, setPortalRoot] = useState(null);
  // User-clicked tab; null means "use requestedTabId or the first visible tab".
  const [selectedTabId, setSelectedTabId] = useState(null);
  const currentUser = useSelector(state => state.user?.currentUser);

  const handleClose = useCallback(
    event => {
      if (onManageDisableScrolling) {
        onManageDisableScrolling(HELP_FLYOUT_SCROLL_LOCK_ID, false);
      }
      onClose(event);
    },
    [onClose, onManageDisableScrolling]
  );

  useEffect(() => {
    setPortalRoot(document.getElementById('portal-root'));
  }, []);

  useEffect(() => {
    if (onManageDisableScrolling) {
      onManageDisableScrolling(HELP_FLYOUT_SCROLL_LOCK_ID, !!isOpen);
    }
    return () => {
      if (onManageDisableScrolling) {
        onManageDisableScrolling(HELP_FLYOUT_SCROLL_LOCK_ID, false);
      }
    };
  }, [isOpen, onManageDisableScrolling]);

  useEffect(() => {
    const handleBodyKeyUp = event => {
      if (event.keyCode === KEY_CODE_ESCAPE && isOpen) {
        handleClose(event);
      }
    };
    window.document.body.addEventListener('keyup', handleBodyKeyUp);
    return () => {
      window.document.body.removeEventListener('keyup', handleBodyKeyUp);
    };
  }, [handleClose, isOpen]);

  useEffect(() => {
    const setVh = () => {
      const vh = window.innerHeight * 0.01;
      window.document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    setVh();
    window.addEventListener('resize', setVh);
    return () => {
      window.removeEventListener('resize', setVh);
    };
  }, []);

  useEffect(() => {
    if (isOpen && panelRef.current) {
      panelRef.current.focus();
    }
  }, [isOpen]);

  // Reset tab selection whenever different content is shown.
  useEffect(() => {
    setSelectedTabId(null);
  }, [contentId, isOpen]);

  if (!portalRoot) {
    return null;
  }

  const helpContent = getHelpContent(contentId);
  const title = helpContent?.titleId
    ? intl.formatMessage({ id: helpContent.titleId })
    : intl.formatMessage({ id: 'HelpFlyout.title' });
  const closeLabel = intl.formatMessage({ id: 'HelpFlyout.close' });

  // Optional tab bar: only rendered when the registry entry defines tabs.
  // Tabs can gate their visibility on the current user (e.g. requirements flag).
  const allTabs = helpContent?.tabs || null;
  const visibleTabs = allTabs
    ? allTabs.filter(tab => !tab.isVisible || tab.isVisible(currentUser))
    : null;
  const tabs = visibleTabs && visibleTabs.length > 0 ? visibleTabs : null;
  const preferredTabId = selectedTabId || requestedTabId;
  const activeTab = tabs
    ? tabs.find(tab => tab.id === preferredTabId) || tabs[0]
    : null;
  const activeTabId = activeTab?.id || null;

  // Active tab's component wins; entries without tabs render their component.
  const ContentComponent = activeTab?.component || helpContent?.component;

  const rootClasses = classNames(css.root, { [css.isOpen]: isOpen }, className);

  return (
    <Portal portalRoot={portalRoot}>
      <div
        className={rootClasses}
        aria-hidden={!isOpen}
        data-testid="HelpFlyout"
      >
        {isOpen ? (
          <div className={css.overlay} onClick={handleClose} role="presentation">
            <div
              className={css.panel}
              onClick={event => event.stopPropagation()}
              ref={panelRef}
              tabIndex={-1}
              role="dialog"
              aria-modal="true"
              aria-label={title}
            >
              <button
                type="button"
                onClick={handleClose}
                className={css.edgeToggle}
                title={closeLabel}
                aria-label={closeLabel}
                data-testid="HelpFlyout.edgeToggle"
              >
                <span className={css.edgeToggleLabel} aria-hidden="true">
                  <FormattedMessage id="HelpFlyout.floatingButtonLabel" />
                </span>
              </button>
              <div className={css.focusedDiv}>
                <div className={css.header}>
                  <h2 className={css.title}>{title}</h2>
                  <button
                    type="button"
                    onClick={handleClose}
                    className={css.closeButton}
                    title={closeLabel}
                    aria-label={closeLabel}
                    data-testid="HelpFlyout.close"
                  >
                    <IconClose rootClassName={css.closeIcon} />
                  </button>
                </div>
                {tabs ? (
                  <div className={css.tabs} role="tablist" data-testid="HelpFlyout.tabs">
                    {tabs.map(tab => (
                      <button
                        key={tab.id}
                        type="button"
                        role="tab"
                        aria-selected={tab.id === activeTabId}
                        className={classNames(css.tab, {
                          [css.tabSelected]: tab.id === activeTabId,
                        })}
                        onClick={() => setSelectedTabId(tab.id)}
                        data-testid={`HelpFlyout.tab.${tab.id}`}
                      >
                        <FormattedMessage id={tab.labelId} />
                      </button>
                    ))}
                  </div>
                ) : null}
                <div className={css.body}>
                  {ContentComponent ? (
                    <ContentComponent />
                  ) : (
                    <p className={css.unknownContent}>
                      <FormattedMessage id="HelpFlyout.unknownContent" />
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </Portal>
  );
};

export default HelpFlyout;
